import useDirtyChecker from "../../../hooks/useDirtyChecker";
import { useState } from "react";
import GenericActionBar from "../../../Layouts/GenericActionBar/GenericActionBar";
import Label from "../../../components/FormComponents/Label";
import axiosInstance, { handleApiError } from "../../../services/axiosService";
import { toastSuccess } from "../../../utils/toastUtils";
import SingleSelectBox from "../../../components/FormComponents/SingleSelectBox";
import Input from "../../../components/FormComponents/Input";
import Button from "../../../components/FormComponents/Button";
import Textarea from "../../../components/FormComponents/Textarea";
import CodeEditor from "../../../components/BuilderComponents/CodeEditor/CodeEditor";

const ApiForwardingEditor = ({ file, templateData, setTemplateData }) => {
  const { isDirty, markClean } = useDirtyChecker(templateData);
  const [isSaving, setIsSaving] = useState(false);

  const handleInputChange = (field, value) => {
    setTemplateData((prev) => ({ ...prev, [field]: value }));
  };

  const handleHeaderParameterChange = (index, field, value) => {
    const updated = [...(templateData.header_parameters || [])];
    if (!updated[index]) updated[index] = {};
    updated[index][field] = value;
    setTemplateData((prev) => ({ ...prev, header_parameters: updated }));
  };

  const addHeaderParameter = () => {
    setTemplateData((prev) => ({
      ...prev,
      header_parameters: [...(prev.header_parameters || []), { key: "", value: "" }]
    }));
  };

  const handleJobTypeChange = (value) => {
    setTemplateData((prev) => ({ ...prev, job_type: value }));
  };

  const handleSaveTemplate = async () => {
    try {
      setIsSaving(true);
      const payload = { ...templateData, name: file?.name };
      const { message } = await axiosInstance.patch(`/api-forwarding-template/${file.id}`, payload);
      toastSuccess(message || "Template saved successfully");
      markClean();
    } catch (err) {
      handleApiError(err);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="d_flex flex_column">
      <GenericActionBar
        path={
          <div>
            <em>API Forwarding</em> → <span>{file.name}</span>
          </div>
        }
        actions={[
          {
            key: "save",
            icon: "save",
            title: isSaving ? "Saving..." : "Save",
            onClick: isSaving ? undefined : handleSaveTemplate
          }
        ]}
        options={{
          save: {
            className: `${isDirty ? "save_glowing" : ""} ${isSaving ? "disabled" : ""}`.trim()
          }
        }}
      />

      <div className="template_form_outer builder_form">
        <Label>Job Type</Label>
        <div className="d_flex gap_2">
          {[
            { value: "api_egress", label: "API Forwarding" },
            { value: "code_execution", label: "Code Execution" }
          ].map((type) => (
            <div key={type.value} className="d_flex gap_1">
              <input
                type="radio"
                id={type.value}
                name="job_type"
                value={type.value}
                checked={templateData?.job_type === type.value}
                onChange={(e) => handleJobTypeChange(e.target.value)}
              />
              <label htmlFor={type.value}>{type.label}</label>
            </div>
          ))}
        </div>

        {templateData?.job_type === "api_egress" && (
          <ApiForwardingForm
            templateData={templateData}
            handleInputChange={handleInputChange}
            handleHeaderParameterChange={handleHeaderParameterChange}
            addHeaderParameter={addHeaderParameter}
          />
        )}

        {templateData?.job_type === "code_execution" && (
          <CodeExecutionForm templateData={templateData} handleInputChange={handleInputChange} />
        )}
      </div>
    </div>
  );
};

const ApiForwardingForm = ({ templateData, handleInputChange, handleHeaderParameterChange, addHeaderParameter }) => (
  <div className="builder_form">
    <SingleSelectBox
      label="HTTP Method Type"
      options={["GET", "POST", "PUT", "DELETE"].map((v) => ({ id: v, name: v }))}
      selectedData={templateData?.http_method_type}
      onChange={(selected) => handleInputChange("http_method_type", selected.id)}
      placeholder="Select Method"
    />

    <Input
      label="Zino Path"
      name="zino_path"
      value={templateData?.zino_path || ""}
      onChange={(e) => handleInputChange("zino_path", e.target.value)}
    />

    <Input
      label="Destination URL"
      name="destination_url"
      value={templateData?.destination_url || ""}
      onChange={(e) => handleInputChange("destination_url", e.target.value)}
    />

    <Label>Header Parameters</Label>
    {(templateData?.header_parameters || []).map((header, index) => (
      <div key={index} className="d_flex gap_2" style={{ width: "100%" }}>
        <div style={{ width: "30%" }}>
          <Input
            placeholder="Key"
            value={header.key}
            onChange={(e) => handleHeaderParameterChange(index, "key", e.target.value)}
          />
        </div>
        <div style={{ width: "60%" }}>
          <Input
            placeholder="Value"
            value={header.value}
            onChange={(e) => handleHeaderParameterChange(index, "value", e.target.value)}
          />
        </div>
      </div>
    ))}
    <Button onClick={addHeaderParameter}>Add Header</Button>
  </div>
);

const CodeExecutionForm = ({ templateData, handleInputChange }) => (
  <div className="builder_form">
    <Textarea
      label="Description"
      name="description"
      value={templateData?.description}
      onChange={(e) => handleInputChange("description", e.target.value)}
    />

    <SingleSelectBox
      label="Program Language"
      options={[
        { id: "javascript", name: "JavaScript" },
        { id: "go", name: "Go" }
      ]}
      selectedData={templateData?.program_language}
      onChange={(selected) => handleInputChange("program_language", selected.id)}
      placeholder="Select Language"
    />

    <Label>Code Box</Label>
    <CodeEditor
      value={templateData?.code_box || ""}
      handleChange={(newValue) => handleInputChange("code_box", newValue)}
      language={templateData?.program_language || "go"}
      height="300px"
      enableBacktickTrigger={false}
    />
  </div>
);

export default ApiForwardingEditor;
